import React from "react";

// Components
import { InnerSectionsWrapper, Row, InputWrapper } from "./ProductDetails.styles";
import { Wrapper, SectionCard, Column } from "../ProductInfo/ProductInfo.styles";
import { HeaderText } from "../Header/Header.styles";
import Checkbox from "../components/Checkbox";
import InputNumber from "../components/InputNumber";
import { Label, RedText } from "../components/Input/Input.styles";

// Constants
import colors from "css/colors";

// Hooks
import useProductCreateFlow from "../../../../hooks/reduxContainer/fulfillment/productCreateFlow";

// Interfaces
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { startCase } from "lodash-es";
import { pluralize } from "lib/pluralize";

interface Props {
  isEditing: boolean;
  length: number;
  width: number;
  height: number;
  weight: number;
  weight_unit: string;
  tracks_lot_numbers: boolean;
  dangerous: boolean;
  fragile: boolean;
  contains_battery: boolean;
  liquid: boolean;
  tracksExpiryDate: boolean;
  daysBeforeExpiryToBlockFulfillment?: number;
  platform?: string;
}

const ProductDetails: React.FC<Props> = ({
  isEditing,
  length,
  width,
  height,
  weight,
  weight_unit,
  tracks_lot_numbers,
  dangerous,
  fragile,
  contains_battery,
  liquid,
  tracksExpiryDate,
  daysBeforeExpiryToBlockFulfillment = 0,
  platform,
}) => {
  const {
    dispatchSetLength,
    dispatchSetWidth,
    dispatchSetHeight,
    dispatchSetWeight,
    dispatchSetLotProduct,
    dispatchSetDangerous,
    dispatchSetFragile,
    dispatchSetContainsBattery,
    dispatchSetLiquidProduct,
    dispatchSetTracksExpiryDate,
    dispatchSetDaysBeforeExpiryToBlockFulfillment,
  } = useProductCreateFlow();

  const renderInputNumber = (
    label: string,
    dimension: string,
    defaultValue: number,
    handleChange: (value?: string | number) => void,
    disallowedCharacters?: string[]
  ) => (
    <InputWrapper>
      <InputNumber
        key={`${label} ${defaultValue}`}
        disabled={!isEditing}
        placeholder={dimension}
        dimension={dimension}
        label={label}
        required
        defaultValue={defaultValue}
        handleChange={handleChange}
        displayOnly={!isEditing || (label === "Weight" && platform === "shopify")}
        precision={1}
        disallowedCharacters={disallowedCharacters || ["-"]}
      />
    </InputWrapper>
  );

  const renderCheckbox = (
    handleOnChange: (checkedValue: CheckboxChangeEvent) => void,
    label: string,
    checked: boolean
  ) => (
    <Checkbox
      key={checked ? `${label}-checked` : `${label}-unchecked`}
      disabled={!isEditing}
      handleOnChange={handleOnChange}
      label={label}
      checked={checked}
      minWidth="206px"
      labelColor={!isEditing && !checked ? colors.GREY : colors.CHARCOAL}
      fontWeight={checked ? 600 : 500}
    />
  );

  return (
    <Wrapper>
      <HeaderText margin="0 0 16px">PRODUCT DETAILS</HeaderText>
      <InnerSectionsWrapper>
        <SectionCard flex={2}>
          <Column>
            {isEditing ? (
              <Label>
                Dimensions (inches)<RedText>*</RedText>
              </Label>
            ) : null}
            <Row>
              {renderInputNumber("Length", "inches", length, (value) => dispatchSetLength(Number(value || 0)))}
              {renderInputNumber("Width", "inches", width, (value) => dispatchSetWidth(Number(value || 0)))}
              {renderInputNumber("Height", "inches", height, (value) => dispatchSetHeight(Number(value || 0)))}
            </Row>
            {isEditing && platform !== "shopify" ? (
              <Label>
                Weight ({weight_unit})<RedText>*</RedText>
              </Label>
            ) : null}
            <Row>
              {renderInputNumber("Weight", weight_unit, weight, (value) => dispatchSetWeight(Number(value || 0)))}
            </Row>
            {platform === "shopify" ? (
              <Row>
                This product was synced from {startCase(platform)}. To edit the weight, please ask the customer to
                modify this data on their platform.
              </Row>
            ) : null}
          </Column>
        </SectionCard>
        <SectionCard flex={3}>
          <Column flex={1}>
            {renderCheckbox(
              (e) => {
                dispatchSetLotProduct(e.target.checked);
              },
              "This is a lot product",
              tracks_lot_numbers
            )}
            {renderCheckbox(
              (e) => {
                dispatchSetDangerous(e.target.checked);
              },
              "This is a dangerous good",
              dangerous
            )}
            {renderCheckbox(
              (e) => {
                dispatchSetFragile(e.target.checked);
              },
              "This is a fragile good",
              fragile
            )}
          </Column>
          <Column flex={1}>
            {renderCheckbox(
              (e) => {
                dispatchSetContainsBattery(e.target.checked);
              },
              "This product contains battery",
              contains_battery
            )}
            {renderCheckbox(
              (e) => {
                dispatchSetLiquidProduct(e.target.checked);
              },
              "This product is liquid",
              liquid
            )}
            {renderCheckbox(
              (e) => {
                dispatchSetTracksExpiryDate(e.target.checked);
              },
              "This product is perishable",
              tracksExpiryDate
            )}
            {tracksExpiryDate && (
              <div style={{ margin: "-16px 0 0 24px" }}>
                {isEditing ? (
                  <>
                    <div style={{ fontSize: "11px", marginBottom: "8px" }}>
                      How many days before expiry should fulfillment be blocked?
                    </div>
                    {renderInputNumber(
                      "Days",
                      "",
                      daysBeforeExpiryToBlockFulfillment,
                      (value) => dispatchSetDaysBeforeExpiryToBlockFulfillment(Number(value || 0)),
                      ["-", ".", "e"]
                    )}
                  </>
                ) : (
                  <div>
                    Fulfillment blocked {daysBeforeExpiryToBlockFulfillment}{" "}
                    {pluralize(daysBeforeExpiryToBlockFulfillment, "day")} before expiration
                  </div>
                )}
              </div>
            )}{" "}
          </Column>
        </SectionCard>
      </InnerSectionsWrapper>
    </Wrapper>
  );
};

export default ProductDetails;
